import { useNavigate, useParams } from 'react-router-dom';
import './update-news.css';
import { useUI } from '../../UIContext';
import { useEffect, useState } from 'react';
import { get, getBlob, post, put, remove } from '../../services/api';
import { handleErr } from '../../utils/functions';
import CloseButton from '../../components/CloseButton';
import PhotoInput from '../../components/PhotoInput';
import Input from '../../components/Input';
import ConfirmationModal from '../../components/ConfirmationModal';
import TextArea from '../../components/TextArea';

function UpdateNews() {
    const { id } = useParams();

    const navigate = useNavigate();

    const { setMessage, setLoading } = useUI();
    const [news, setNews] = useState({});
    const [image, setImage] = useState(undefined);
    const [submitAttempt, setSubmitAttempt] = useState(false);
    const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
    const [hasSavedProcess, setHasSavedProcess] = useState(false);

    function isTitleValid() {
        return !!news.title && news.title.length >= 2 && news.title.length <= 56;
    }

    function isDescriptionValid() {
        return !!news.description && news.description.length >= 2 && news.description.length <= 200;
    }

    useEffect(() => {
        async function getNewsImage() {
            await getBlob(`/news/${id}/image`, true, setMessage,
                (response) => {
                    setImage(response.data);
                },
                (err) => {
                    handleErr(setMessage, `Falha ao carregar a image da notícia: ${err}`);
                },
                () => {
                    setLoading(false);
                }
            );
        }

        async function getNewsData() {
            setLoading(true);

            await get(`news/${id}`, true, setMessage,
                async (response) => {
                    const image = {
                        path: response.data.imagePath,
                        changed: false
                    };

                    setNews({ 
                        ...response.data,
                        image: image
                    });

                    if (!image.path) {
                        setLoading(false);
                        return;
                    }

                    await getNewsImage();
                },
                (err) => {
                    handleErr(setMessage, `Não foi possível carregar a notícia: ${err}`);
                }
            )
        }

        getNewsData();
    }, [id, setNews, setLoading, setMessage]);

    function showSuccess() {
        setHasSavedProcess(true);
        setMessage({ type: 'success', text: 'A notícia foi modificada.' });
    }

    async function addImage() {
        const formData = new FormData();
        formData.append('image', image)
        await post(`/news/${id}/image`, formData, true, setMessage,
            () => {
                showSuccess();
            },
            (err) => {
                handleErr(setMessage, `Falha ao salvar os dados da notícia: ${err}`);
            },
            () => {
                setLoading(false);
            }
        );
    }

    async function removeImage() {
        await remove(`/news/${id}/image`, true, setMessage,
            () => {
                showSuccess();
            },
            (err) => {
                handleErr(setMessage, `Falha ao salvar os dados da notícia: ${err}`);
            },
            () => {
                setLoading(false);
            }
        );
    }

    async function onClickSave(e) {

        function isSaveValid() {
            setSubmitAttempt(true);

            return !!news.title && !!news.description;
        }

        e.preventDefault();

        if (!isSaveValid())
            return;

        setLoading(true);

        const data = {
            title: news.title,
            description: news.description,
        };

        const shouldContinueLoading = !!news.image?.changed || (!!news.image?.path && !image);

        await put(`news/${news.id}`, data, true, setMessage,
            async () => {
                if (!!news.image?.changed)
                    await addImage();
                else if (!!news.image?.path && !image)
                    await removeImage();
                else {
                    showSuccess();
                    setLoading(false);
                }
            },
            (err) => {
                handleErr(setMessage, `Não foi possível atualizar a notícia: ${err}`);
                setLoading(false);
            },
            () => {
                if (!shouldContinueLoading)
                    setLoading(false);
            }
        );
    }

    function onChangeImageUpload(file) {
        setImage(file);
        setNews({
            ...news,
            image: {
                ...news.image,
                changed: true
            }
        });
    }

    function onRemoveImage() {
        setImage(undefined);
        setNews({
            ...news,
            image: {
                ...news.image,
                changed: false
            }
        });
    }

    function onClickRemove(e) {
        e.preventDefault();
        
        setShowDeleteConfirmation(true);
    }

    async function removeNews() {
        setShowDeleteConfirmation(false);
        setLoading(true);

        await remove(`news/${news.id}`, true, setMessage,
            () => {
                navigate('/news');
                setMessage({ type: 'success', text: 'A notícia foi removida.' });
            },
            (err) => {
                handleErr(setMessage, `Não foi possível remover a notícia: ${err}`);
            },
            () => {
                setLoading(false);
            }
        );
    }

    return (
        <>
            <form className='update-news-form'>
                <header className='container-header'>
                    <h1>Editar Notícia</h1>

                    <CloseButton
                        onClick={() => navigate('/news')}
                        shouldConfirm={!hasSavedProcess}
                    />
                </header>

                <div className='news-image-container'>
                    <PhotoInput
                        photo={image}
                        onChange={onChangeImageUpload}
                        onRemove={onRemoveImage}
                    />
                </div>

                <Input
                    className='commom-input glass-effect-15'
                    type='text'
                    value={news.title}
                    onChange={(e) => setNews({
                        ...news,
                        title: e.target.value
                    })}
                    onValidate={isTitleValid}
                    placeholder='Título'
                    shouldValidate={submitAttempt}
                    maxLength={56}
                    warningMsg='O título deve conter ao menos 2 caracteres.'
                />

                <TextArea
                    cols='4'
                    rows='4'
                    className='commom-input glass-effect-15'
                    value={news.description}
                    onChange={(e) => setNews({
                        ...news,
                        description: e.target.value
                    })}
                    onValidate={isDescriptionValid}
                    placeholder='Descrição'
                    shouldValidate={submitAttempt}
                    maxLength={20000}
                    warningMsg='A descrição deve conter ao menos 5 caracteres.'
                />

                <div className='update-news-buttons'>
                    <button
                        type='submit'
                        onClick={onClickRemove}
                        className='remove-news red-button'>
                        Excluir
                    </button>

                    <button
                        type='submit'
                        onClick={onClickSave}
                        className='blue-button zoom-effect'>
                        Salvar
                    </button>
                </div>
            </form>

            <ConfirmationModal
                show={showDeleteConfirmation}
                title='Excluir a notícia'
                description='Atenção! Esta opção é irreversível. Você realmente deseja excluir a notícia?'
                onConfirm={() => removeNews()}
                onClose={() => setShowDeleteConfirmation(false)}
            />
        </>
    );
}

export default UpdateNews;